import {
  AppSumoGateLoading,
  AppSumoUpgradeGate,
} from '@/components/appsumo/appsumo-upgrade-gate.component';
import {
  APPSUMO_PRO_TILES,
  PRO_TILES,
  tilesForPlan,
  type PlanTile,
} from '@/components/upgrade/plan-tiles';
import { UpgradeCard } from '@/components/upgrade/upgrade-card.component';
import {
  usePlanGateRoute,
  useStartCardTrial,
} from '@/components/upgrade/use-plan-gate.hook';
import { useAppSumoUpgrade } from '@/hooks/use-appsumo-upgrade.hook';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { usePageRestored } from '@/hooks/use-page-restored.hook';
import { withCleanlistPlanName } from '@/lib/appsumo-upgrade';
import { formatRenewalPrice } from '@/lib/card-trial';
import { toUpgradeTier } from '@/lib/upgrade-link';
import { useState } from 'react';

/**
 * A page the workspace's plan doesn't include, as the upgrade card. The way
 * in is the Pro trial when the workspace can still start it and the trial
 * includes the page, the plans page (the upgrade wizard on the v4 catalog)
 * otherwise; an AppSumo plan names itself and goes there too, or to its
 * upgrade offer while that is open.
 */
interface PlanGateProps {
  title: string;
  subtitle?: string;
  /** The plan the page needs, e.g. "Starter". */
  plan: string;
  /** The Pro trial doesn't include the page (the public API). */
  notInTrial?: boolean;
  secondary?: { label: string; href: string };
  /** The workspace is on an AppSumo plan alone. */
  appSumo?: boolean;
  /** Instead of the plan's own tiles. */
  tiles?: PlanTile[];
  className?: string;
}

export function PlanGate(props: PlanGateProps) {
  const { route, v4 } = usePlanGateRoute({
    plan: props.plan,
    notInTrial: props.notInTrial,
    appSumo: props.appSumo,
  });
  if (route === 'trial') return <TrialPlanGate {...props} v4={v4} />;
  if (route === 'appsumo') return <AppSumoPlanGate {...props} v4={v4} />;
  return (
    <UpgradeCard
      title={props.title}
      subtitle={props.subtitle}
      tiles={props.tiles ?? tilesForPlan(props.plan)}
      compare={v4 ? { plan: props.plan } : undefined}
      action={{
        label: `Upgrade to ${props.plan}`,
        plan: toUpgradeTier(props.plan),
      }}
      secondary={props.secondary}
      className={props.className}
    />
  );
}

/**
 * A plan's tiles as an AppSumo-only workspace reads them: Pro without the
 * LinkedIn mark or the CRM and API tiles (tiers 5 to 7 already have those).
 * Starter's are the regular ones, 1,000 searches a month included.
 */
function appSumoTilesForPlan(plan: string): PlanTile[] {
  const key = plan.trim().toLowerCase();
  if (key === 'pro') return APPSUMO_PRO_TILES;
  return tilesForPlan(plan);
}

/**
 * An AppSumo plan alone. While the AppSumo upgrade price is open, the page is
 * that offer under its own title (the copy deck's "anything else locked");
 * otherwise the card names the plan and goes to the plans page. Either way
 * the title calls the plan "Cleanlist Pro" (or Starter), since AppSumo's own
 * tiers are called Starter and Pro too. Its own component so the offer is
 * only asked for where it can apply.
 */
function AppSumoPlanGate({
  title,
  subtitle,
  plan,
  tiles,
  secondary,
  className,
  v4,
}: PlanGateProps & { v4: boolean }) {
  const { offer, prices, phase, active, isLoading } = useAppSumoUpgrade();
  if (isLoading) return <AppSumoGateLoading />;
  if (active && offer && prices) {
    return (
      <AppSumoUpgradeGate
        feature='generic'
        offer={offer}
        prices={prices}
        phase={phase}
        title={title}
        className={className}
      />
    );
  }
  return (
    <UpgradeCard
      title={withCleanlistPlanName(title)}
      subtitle={subtitle}
      tiles={tiles ?? appSumoTilesForPlan(plan)}
      compare={v4 ? { plan } : undefined}
      action={{ label: 'See plans', plan: toUpgradeTier(plan) }}
      secondary={secondary}
      className={className}
    />
  );
}

/** The card with the Pro trial as its way in, Checkout started from here. */
function TrialPlanGate({
  title,
  subtitle,
  secondary,
  className,
  v4,
}: PlanGateProps & { v4: boolean }) {
  const { trial, organizationId } = useFeatureAccess();
  const start = useStartCardTrial();
  // Busy for the workspace whose Checkout is opening, so switching to another
  // workspace never inherits a disabled button.
  const [busyFor, setBusyFor] = useState<string | null>(null);
  const busy = busyFor !== null && busyFor === organizationId;
  const renewal = trial?.renews_to;
  // Browser Back from Checkout: the page returns with the spinner still on.
  usePageRestored(() => setBusyFor(null));

  const startTrial = async () => {
    if (busy) return;
    let startedFor: string | null = null;
    const opened = await start((id) => {
      startedFor = id;
      setBusyFor(id);
    });
    if (!opened && startedFor !== null) {
      const id: string = startedFor;
      setBusyFor((current) => (current === id ? null : current));
    }
  };

  return (
    <UpgradeCard
      title={title}
      subtitle={subtitle}
      tiles={v4 ? PRO_TILES : []}
      compare={v4 ? { plan: 'Pro', trial: true } : undefined}
      action={{
        label: 'Start free 14-day trial',
        onClick: () => void startTrial(),
        busy,
      }}
      secondary={secondary}
      legal={
        renewal
          ? `${formatRenewalPrice(renewal)} after your trial ends. Cancel anytime before and you won't be charged.`
          : undefined
      }
      className={className}
    />
  );
}
